// TODO columns should come from BE
// const dataStructure = {
//   key: "",
//   order: 0,
//   label: "",
//   enableResizing: true,
//   enableSorting: true,
//   size: 200,
// };

import CopyableText from "@/components/CopyableText";
import StatusBadge from "@/components/StatusBadge";
import { booleanOptionIconLabels } from "@/constant/GeneralOptions/boolean";
import { DATE_FORMAT } from "@/constant/Tables/date-format";
import { userStatusBgColors, userStatusLabels } from "@/constant/Users/status";
import { userTypeLabels } from "@/constant/Users/types";
import { formatNumber, truncate } from "@/utils/helper";
import Actions from "./Actions";

export const columns = (columnConfigs = null) => {
  const booleanOptionLabelsMap = booleanOptionIconLabels();
  const userStatusLabelMap = userStatusLabels;
  const userStatusBgColorMap = userStatusBgColors;
  const userTypeLabelMap = userTypeLabels();

  const dateFormat = columnConfigs?.date_format;

  const allColumns = [
    {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => <Actions row={row} />,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge
          status={row.getValue("status")}
          statusLabelMap={userStatusLabelMap}
          statusBgColorMap={userStatusBgColorMap}
        />
      ),
    },
    {
      accessorKey: "first_name",
      header: "First Name",
    },
    {
      accessorKey: "middle_name",
      header: "Middle Name",
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
    },
    {
      accessorKey: "username",
      header: "Username",
    },
    {
      id: "type",
      accessorFn: (row) => userTypeLabelMap[row.type] ?? row.type,
      header: "User Type",
      enableSorting: false,
    },
    {
      accessorKey: "is_partner",
      header: "Partner",
      accessorFn: (row) => booleanOptionLabelsMap[row.is_partner],
      // enableSorting: false,
    },
    {
      accessorKey: "user_business_profile.business_name",
      header: "Business Name",
      enableSorting: false,
    },
    {
      accessorKey: "user_business_profile.registration_number",
      header: "Registration Number",
      enableSorting: false,
    },
    {
      accessorKey: "user_business_profile.business_type",
      header: "Business Type",
      enableSorting: false,
    },
    {
      id: "website_url",
      cell: ({ row }) => {
        return (
          <CopyableText
            text={row.original.website_url}
            message={"URL copied successfully"}
          >
            {truncate(row.original.website_url)}
          </CopyableText>
        );
      },
      header: "Website URL",
    },
    {
      accessorKey: "primary_address.country.name",
      header: "Country",
      enableSorting: false,
    },
    {
      id: "age",
      accessorFn: (row) => {
        if (!row.birth_date) return "-";

        const birthDate = new Date(row.birth_date);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();

        // Adjust the age if the birthday hasn't occurred yet this year
        if (
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < birthDate.getDate())
        ) {
          return age - 1;
        }

        return age;
      },
      header: "Age",
    },
    {
      id: "rating",
      accessorFn: (row) => (row.rating ? `⭐ ${row.rating}` : "-"),
      header: "Rating",
    },
    {
      id: "total_likes",
      header: "Likes",
      accessorFn: (row) => <div>{row.likes?.total_likes || 0}</div>,
    },
    {
      id: "total_shares",
      header: "Shares",
      accessorFn: (row) => <div>{row.shares?.total_shares || 0}</div>,
    },
    {
      id: "total_views",
      header: "Views",
      accessorFn: (row) => <div>{row.views?.total_views || 0}</div>,
    },
    {
      id: "email",
      accessorKey: "email",
      header: "Email",
    },
    {
      id: "two_factor_auth_enabled",
      accessorFn: (row) => booleanOptionLabelsMap[row.two_factor_auth_enabled],
      header: "2-Factor Auth?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      id: "newsletter_subscription",
      accessorFn: (row) => booleanOptionLabelsMap[row.newsletter_subscription],
      header: "Newsletter?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      id: "cookies_accepted",
      accessorFn: (row) => booleanOptionLabelsMap[row.cookies_accepted],
      header: "Cookies?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      id: "birth_date_business_registration_date",
      header: "Date of Birth / Reg.",
      cell: ({ row }) => {
        const birthDate = row.original.birth_date;
        const registrationDate =
          row.original.user_business_profile?.business_registration_date;
        let rawDate = birthDate ?? registrationDate;

        if (!rawDate) return "-";

        [rawDate] = rawDate.split("T");
        let [year, month, day] = rawDate.split("-");
        let date = new Date(Date.UTC(year, month - 1, day));

        year = date.getFullYear();
        month = String(date.getMonth() + 1).padStart(2, "0");
        day = String(date.getDate()).padStart(2, "0");

        if (dateFormat === DATE_FORMAT.MMDDYYYY) {
          return `${month}/${day}/${year}`;
        } else if (dateFormat === DATE_FORMAT.YYYYMMDD) {
          return `${year}/${month}/${day}`;
        }

        return "-";
      },
      enableSorting: false,
    },
    {
      accessorKey: "tax_id",
      header: "Tax ID",
    },
    {
      accessorKey: "primary_address.address_line_1",
      header: "Address Line 1",
      enableSorting: false,
    },
    {
      accessorKey: "primary_address.address_line_2",
      header: "Address Line 2",
      enableSorting: false,
    },
    {
      accessorKey: "primary_address.city",
      header: "City",
      enableSorting: false,
    },
    {
      accessorKey: "primary_address.zip_code",
      header: "Zip Code",
      enableSorting: false,
    },
    {
      accessorKey: "primary_address.state",
      header: "State",
      enableSorting: false,
    },
    {
      id: "phone",
      accessorFn: (row) => row.phone?.replace("-", ""),
      header: "Phone",
      enableSorting: false,
    },
    {
      id: "buyer_metric.purchases_lifetime",
      accessorFn: (row) =>
        formatNumber(row.buyer_metric?.purchases_lifetime, false, 0),
      header: "# Purchases Lifetime",
      enableSorting: false,
      size: 80,
    },
    {
      id: "buyer_metric.purchases_ytd",
      accessorFn: (row) =>
        formatNumber(row.buyer_metric?.purchases_ytd, false, 0),
      header: "# Purchases YTD",
      enableSorting: false,
      size: 80,
    },
    {
      id: "buyer_metric.purchases_py",
      accessorFn: (row) =>
        formatNumber(row.buyer_metric?.purchases_py, false, 0),
      header: "# Purchases PY",
      enableSorting: false,
      size: 80,
    },
    {
      id: "seller_metric.sales_lifetime",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.sales_lifetime, false, 0),
      header: "# Sales Lifetime",
      enableSorting: false,
      size: 80,
    },
    {
      id: "seller_metric.sales_ytd",
      accessorFn: (row) => formatNumber(row.seller_metric?.sales_ytd, false, 0),
      header: "# Sales YTD",
      enableSorting: false,
      size: 80,
    },
    {
      id: "seller_metric.sales_py",
      accessorFn: (row) => formatNumber(row.seller_metric?.sales_py, false, 0),
      header: "# Sales PY",
      enableSorting: false,
      size: 80,
    },
    {
      id: "seller_metric.jersium_commission_lifetime",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_commission_lifetime, true),
      header: "Jersium Commission Lifetime",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.jersium_commission_ytd",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_commission_ytd, true),
      header: "Jersium Commission YTD",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.jersium_commission_py",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_commission_py, true),
      header: "Jersium Commission PY",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.jersium_tax_collected_lifetime",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_tax_collected_lifetime, true),
      header: "Jersium Tax Collected Lifetime",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.jersium_tax_collected_ytd",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_tax_collected_ytd, true),
      header: "Jersium Tax Collected YTD",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.jersium_tax_collected_py",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.jersium_tax_collected_py, true),
      header: "Jersium Tax Collected PY",
      size: 150,
      enableSorting: false,
    },
    {
      id: "seller_metric.sales_amount_lifetime",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.sales_amount_lifetime, true),
      header: "Sales Amount Lifetime",
      enableSorting: false,
    },
    {
      id: "seller_metric.sales_amount_ytd",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.sales_amount_ytd, true),
      header: "Sales Amount YTD",
      enableSorting: false,
    },
    {
      id: "seller_metric.sales_amount_py",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.sales_amount_py, true),
      header: "Sales Amount PY",
      enableSorting: false,
    },
    {
      id: "seller_metric.number_of_ship_to_countries",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.number_of_ship_to_countries, false, 0),
      header: "# Ship-to Countries",
      enableSorting: false,
      size: 80,
    },
    {
      id: "seller_metric.avg_dispatch_time",
      accessorFn: (row) =>
        formatNumber(row.seller_metric?.avg_dispatch_time, false, 0),
      header: "Avg. Dispatch Time",
      enableSorting: false,
    },
    {
      id: "social_metric.collection_items",
      accessorFn: (row) =>
        formatNumber(row.social_metric?.collection_items, false, 0),
      header: "# Collection Items",
      enableSorting: false,
      size: 80,
    },
    {
      id: "social_metric.imported_collection_items",
      accessorFn: (row) =>
        formatNumber(row.social_metric?.imported_collection_items, false, 0),
      header: "Imported Collection Items",
      size: 150,
      enableSorting: false,
    },
    {
      id: "social_metric.friends",
      accessorFn: (row) => formatNumber(row.social_metric?.friends, false, 0),
      header: "# Friends",
      enableSorting: false,
      size: 80,
    },
    {
      id: "social_metric.image_uploads_total",
      accessorFn: (row) =>
        formatNumber(row.social_metric?.image_uploads_total, false, 0),
      header: "Image Uploads Total",
      enableSorting: false,
    },
    {
      id: "social_metric.images_on_profile",
      accessorFn: (row) =>
        formatNumber(row.social_metric?.images_on_profile, false, 0),
      header: "Images On Profile",
      enableSorting: false,
    },
    {
      id: "social_metric.featured_images",
      accessorFn: (row) =>
        formatNumber(row.social_metric?.featured_images, false, 0),
      header: "Featured Images",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_team_fb.name",
      header: "Favorite Team FB",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_national_team_fb.name",
      header: "Favorite NT FB",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_team_af.name",
      header: "Favorite Team AF",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_national_team_af.name",
      header: "Favorite NT AF",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_team_bb.name",
      header: "Favorite Team BB",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_national_team_bb.name",
      header: "Favorite NT BB",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_team_bs.name",
      header: "Favorite Team BS",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_national_team_bs.name",
      header: "Favorite NT BS",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_team_ih.name",
      header: "Favorite Team IH",
      enableSorting: false,
    },
    {
      accessorKey: "social_metric.favorite_national_team_ih.name",
      header: "Favorite NT IH",
      enableSorting: false,
    },
    {
      id: "term_conditions_accepted_at",
      accessorFn: (row) =>
        booleanOptionLabelsMap[row.term_conditions_accepted_at],
      header: "T&C?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      id: "privacy_policy_accepted_at",
      accessorFn: (row) =>
        booleanOptionLabelsMap[row.privacy_policy_accepted_at],
      header: "Privacy Policy?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      id: "preferred_sport.name",
      accessorFn: (row) => row.preferred_sport?.name,
      header: "Preferred Sport",
    },
    {
      id: "preferred_currency.name",
      accessorFn: (row) => row.preferred_currency?.name,
      header: "Preferred Currency",
    },
    {
      id: "preferred_language.title",
      accessorFn: (row) => row.preferred_language?.title,
      header: "Preferred Language",
    },
    {
      id: "ship_to_countries",
      accessorFn: (row) => {
        if (
          Array.isArray(row.ship_to_countries) &&
          row.ship_to_countries.length > 0
        ) {
          return row.ship_to_countries
            .map((shipToCountry) => shipToCountry.country.code)
            .join("; ");
        }
        return "-";
      },
      header: "Ship-to Countries",
      enableSorting: false,
    },
    {
      id: "admin_remarks",
      cell: ({ row }) => {
        return (
          <CopyableText text={row.original.admin_remarks}>
            {truncate(row.original.admin_remarks)}
          </CopyableText>
        );
      },
      header: "Admin Notes",
    },
    {
      id: "reason_for_deletion",
      cell: ({ row }) => {
        return (
          <CopyableText text={row.original.reason_for_deletion}>
            {truncate(row.original.reason_for_deletion)}
          </CopyableText>
        );
      },
      header: "Reason for Deletion",
    },
    {
      id: "platform_fee",
      accessorKey: "platform_fee",
      header: "Selling Fee %",
    },
  ];

  const filteredColumns = columnConfigs
    ? allColumns
        .filter((column) =>
          columnConfigs?.columns?.includes(column.accessorKey ?? column.id)
        )
        .sort((a, b) => {
          const selectedColumns = columnConfigs?.columns;
          const indexA = selectedColumns.indexOf(a.accessorKey ?? a.id);
          const indexB = selectedColumns.indexOf(b.accessorKey ?? b.id);
          return indexA - indexB;
        })
    : allColumns;

  return filteredColumns;
};
